import { ref } from 'vue'
import type { OrganisationCredits } from '@/types/credits'
import { adjustOrganisationCredits, fetchOrganisationCredits } from '@/api/credits'

export function useOrganisationCredits() {
  const credits = ref<OrganisationCredits | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const actionLoading = ref(false)
  const actionError = ref<string | null>(null)
  const fieldErrors = ref<Record<string, string[]>>({})

  async function fetchCredits(organisationId: number): Promise<void> {
    loading.value = true
    error.value = null
    try {
      credits.value = await fetchOrganisationCredits(organisationId)
    } catch {
      error.value = 'admin.common.error_loading'
    } finally {
      loading.value = false
    }
  }

  // The POST response has the same shape as the GET, so it replaces local state.
  async function adjustCredits(organisationId: number, amount: number): Promise<boolean> {
    actionLoading.value = true
    actionError.value = null
    fieldErrors.value = {}
    try {
      credits.value = await adjustOrganisationCredits(organisationId, amount)
      return true
    } catch (e: unknown) {
      if (is422Error(e)) {
        fieldErrors.value = extractFieldErrors(e) ?? {}
      } else {
        actionError.value = 'admin.common.error_generic'
      }
      return false
    } finally {
      actionLoading.value = false
    }
  }

  return {
    credits,
    loading,
    error,
    actionLoading,
    actionError,
    fieldErrors,
    fetchCredits,
    adjustCredits,
  }
}

function is422Error(e: unknown): boolean {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: { status: number } }).response.status === 422
  )
}

function extractFieldErrors(e: unknown): Record<string, string[]> | null {
  if (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object'
  ) {
    const resp = (e as { response: { data?: { errors?: Record<string, string[]> } } }).response
    return resp.data?.errors ?? null
  }
  return null
}
